// src/pages/CourseDetail.js
//
// REESCRITO — ya no lee de ../data/courses (estático) ni de
// enrolledCourses del progressStore. Ahora consume cursoService.getBySlug()
// (endpoint nuevo en el backend) y aprendizajeService.matricular().
// El estado "inscrito" se resuelve consultando meService.getMisCursos()
// una sola vez al montar.
//
// El backend no tiene concepto de "precio" ni "rating" (EducaT es un
// LMS, no un marketplace) — esas secciones se quitaron.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  ArrowBack,
  MenuBook,
  Groups,
  EmojiEvents,
  PlayCircle,
  ExpandMore,
  Description,
  Quiz,
  Assignment,
  School,
  CheckCircle,
  VideoCall,
  OndemandVideo,
  Forum,
} from '@mui/icons-material';
import { useAuth } from '../context/AppContext';
import { cursoService, aprendizajeService, meService } from '../services/api';

const TYPE_ICONS = {
  Material: Description,
  Video: PlayCircle,
  Quiz: Quiz,
  Actividad: Assignment,
  Examen: School,
  SesionOnline: VideoCall,
  SesionGrabada: OndemandVideo,
  Foro: Forum,
};

const TYPE_LABELS = {
  Material: 'Material',
  Video: 'Video',
  Quiz: 'Cuestionario',
  Actividad: 'Actividad',
  Examen: 'Examen',
  SesionOnline: 'Sesión online',
  SesionGrabada: 'Sesión grabada',
  Foro: 'Foro',
};

const NIVEL_LABELS = { Basico: 'Básico', Intermedio: 'Intermedio', Avanzado: 'Avanzado' };

function CourseDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // 401/403 -> el visitante no tiene sesion (o no esta autorizado).
  // Se muestra eso y no "Curso no encontrado", que sugiere un fallo.
  const [sinAcceso, setSinAcceso] = useState(false);
  const [errorCarga, setErrorCarga] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState('');
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const TERMS_VERSION = 'v1';

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    setSinAcceso(false);
    setErrorCarga(null);
    try {
      const data = await cursoService.getBySlug(slug);
      if (!data) { setNotFound(true); setLoading(false); return; }
      setCourse(data);

      if (isAuthenticated) {
        const misCursos = await meService.getMisCursos();
        const match = misCursos.find((c) => c.id === data.id);
        if (match) {
          setIsEnrolled(true);
          setProgress(match.progress || 0);
        }
      }
    } catch (e) {
      if (e?.status === 401 || e?.status === 403) setSinAcceso(true);
      else if (e?.status === 404) setNotFound(true);
      else setErrorCarga(e?.message || 'Intenta de nuevo en unos minutos.');
    } finally {
      setLoading(false);
    }
  }, [slug, isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  const handleEnroll = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/course/${slug}` } });
      return;
    }
    setTermsAccepted(false);
    setEnrollError('');
    setTermsOpen(true);
  };

  const handleConfirmEnroll = async () => {
    if (!termsAccepted) return;
    setEnrolling(true);
    setEnrollError('');
    const result = await aprendizajeService
      .matricular(course.id, null, { aceptados: true, version: TERMS_VERSION })
      .catch((err) => ({ error: err.message }));
    setEnrolling(false);
    if (result?.error) {
      setEnrollError(result.error);
    } else {
      setTermsOpen(false);
      setIsEnrolled(true);
      navigate(`/learn/${course.slug}`);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Skeleton variant="rounded" height={400} />
      </Container>
    );
  }

  if (sinAcceso) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>Inicia sesión para ver este curso</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          El contenido de los cursos está disponible solo para usuarios registrados.
        </Typography>
        <Button variant="contained" sx={{ mr: 2 }}
          onClick={() => navigate('/login', { state: { from: `/course/${slug}` } })}>
          Iniciar sesión
        </Button>
        <Button component={Link} to="/courses" variant="outlined">Ver cursos</Button>
      </Container>
    );
  }

  if (errorCarga) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>No se pudo cargar el curso</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>{errorCarga}</Typography>
        <Button variant="contained" sx={{ mr: 2 }} onClick={() => window.location.reload()}>Reintentar</Button>
        <Button component={Link} to="/courses" variant="outlined">Ver cursos</Button>
      </Container>
    );
  }

  if (notFound || !course) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>Curso no encontrado</Typography>
        <Button component={Link} to="/courses" variant="contained">Ver cursos</Button>
      </Container>
    );
  }

  const allContents = (course.units || []).flatMap((u) => u.contents || []);
  const totalPoints = allContents.reduce((sum, c) => sum + (c.maxPoints || 0), 0);

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Hero */}
      <Box sx={{ background: 'linear-gradient(180deg, rgba(233,69,96,0.1) 0%, transparent 100%)', py: 6 }}>
        <Container maxWidth="lg">
          <Button component={Link} to="/courses" startIcon={<ArrowBack />} sx={{ mb: 3, color: 'text.secondary' }}>
            Volver a cursos
          </Button>

          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                {course.category && <Chip label={course.category} color="primary" variant="outlined" />}
                <Chip label={NIVEL_LABELS[course.level] || course.level} variant="outlined" />
                {course.isGlobal && <Chip label="Red Solidaria" color="info" />}
              </Box>

              <Typography variant="h3" fontWeight={700} gutterBottom>{course.title}</Typography>
              {course.subtitle && (
                <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>{course.subtitle}</Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                  <Groups fontSize="small" />
                  <Typography>{course.totalEnrollments || 0} estudiantes</Typography>
                </Box>
              </Box>

              {course.instructor && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 48, height: 48 }}>{course.instructor[0]}</Avatar>
                  <Box>
                    <Typography fontWeight={600}>{course.instructor}</Typography>
                    <Typography variant="body2" color="text.secondary">{course.institution || 'Instructor'}</Typography>
                  </Box>
                </Box>
              )}
            </Grid>

            {/* Sidebar Card */}
            <Grid item xs={12} md={4}>
              <Card sx={{ position: 'sticky', top: 100 }}>
                <Box
                  component="img"
                  src={course.image || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800'}
                  alt={course.title}
                  sx={{ width: '100%', height: 180, objectFit: 'cover' }}
                />
                <CardContent>
                  {enrollError && <Alert severity="error" sx={{ mb: 2 }}>{enrollError}</Alert>}

                  {isEnrolled ? (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">Tu progreso</Typography>
                          <Typography variant="body2" color="success.main" fontWeight={600}>{Math.round(progress)}%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={progress} color="success" />
                      </Box>
                      <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        startIcon={<PlayCircle />}
                        onClick={() => navigate(`/learn/${course.slug}`)}
                      >
                        Ir al aula virtual
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={enrolling}
                      onClick={handleEnroll}
                    >
                      {enrolling ? 'Inscribiendo…' : 'Inscribirme ahora'}
                    </Button>
                  )}

                  <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                    {[
                      { icon: <MenuBook />, label: 'Contenidos', value: `${allContents.length} actividades` },
                      { icon: <EmojiEvents />, label: 'Puntos totales', value: totalPoints },
                      { icon: <School />, label: 'Créditos', value: course.credits },
                      { icon: <Groups />, label: 'Duración', value: `${course.durationWeeks} semanas` },
                    ].map((item, i) => (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                          {item.icon}
                          <Typography variant="body2">{item.label}</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600}>{item.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        {/* Descripción */}
        {course.description && (
          <>
            <Typography variant="h5" fontWeight={700} gutterBottom>Descripción</Typography>
            <Typography color="text.secondary" sx={{ mb: 6, whiteSpace: 'pre-line' }}>{course.description}</Typography>
          </>
        )}

        {/* Criterios de evaluación */}
        {course.gradingWeights && (
          <>
            <Typography variant="h5" fontWeight={700} gutterBottom>Criterios de evaluación</Typography>
            <Card sx={{ mb: 6 }}>
              <Table>
                <TableBody>
                  {[
                    ['Cuestionarios', course.gradingWeights.quizzes],
                    ['Actividades', course.gradingWeights.activities],
                    ['Examen parcial', course.gradingWeights.midterm],
                    ['Examen final', course.gradingWeights.final],
                    ['Proyecto', course.gradingWeights.project],
                  ].map(([label, value]) => (
                    <TableRow key={label}>
                      <TableCell>{label}</TableCell>
                      <TableCell align="right">
                        <Chip label={`${value}%`} size="small" color="primary" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </>
        )}

        {/* Contenido del curso */}
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Contenido del curso
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {(course.units || []).length} unidades • {allContents.length} actividades
        </Typography>

        {(course.units || []).map((unit, i) => (
          <Accordion key={unit.id} defaultExpanded={i === 0}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={600}>{unit.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {(unit.contents || []).length} contenidos
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <List disablePadding>
                {(unit.contents || []).map((content) => {
                  const Icon = TYPE_ICONS[content.type] || Description;
                  return (
                    <ListItem key={content.id} sx={{ borderTop: 1, borderColor: 'divider' }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Icon color="disabled" />
                      </ListItemIcon>
                      <ListItemText
                        primary={content.title}
                        secondary={TYPE_LABELS[content.type] || content.type}
                      />
                      {content.maxPoints > 0 && (
                        <Chip label={`${content.maxPoints} pts`} size="small" variant="outlined" />
                      )}
                    </ListItem>
                  );
                })}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>

      {/* Diálogo de términos de uso del contenido ───────────────── */}
      <Dialog open={termsOpen} onClose={() => !enrolling && setTermsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Términos de uso del contenido</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div" sx={{ color: 'text.primary', lineHeight: 1.65 }}>
            Antes de inscribirte en <strong>{course?.title}</strong> necesitamos que
            aceptes las siguientes condiciones sobre el uso del contenido educativo:

            <Box component="ul" sx={{ mt: 2, pl: 3 }}>
              <li>
                El contenido del curso (textos, videos, material descargable, evaluaciones
                y recursos complementarios) es <strong>propiedad intelectual</strong> de la
                institución que lo publica y está protegido por la legislación de derechos
                de autor vigente.
              </li>
              <li>
                Tu acceso es <strong>personal e intransferible</strong>. No puedes compartir
                tus credenciales ni facilitar que terceros accedan al contenido a través
                de tu cuenta.
              </li>
              <li>
                No está permitido <strong>reproducir, distribuir, publicar, revender o
                reutilizar</strong> el contenido, total o parcialmente, en otras plataformas,
                redes sociales, repositorios públicos o cursos propios, sin autorización
                escrita de la institución titular.
              </li>
              <li>
                El uso del material queda <strong>registrado y trazado</strong> por sesión,
                incluyendo IP, momento de acceso y marcas forenses del contenido servido,
                para fines de auditoría.
              </li>
              <li>
                El incumplimiento de estas condiciones puede derivar en la{' '}
                <strong>cancelación inmediata</strong> de la matrícula y de los certificados
                emitidos, así como en las acciones legales correspondientes.
              </li>
            </Box>

            <Box sx={{ mt: 2, fontSize: '0.85rem', color: 'text.secondary' }}>
              Versión {TERMS_VERSION} · Al aceptar, se guarda un registro con fecha, hora e IP.
            </Box>
          </DialogContentText>

          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                disabled={enrolling}
              />
            }
            label="He leído y acepto los términos de uso del contenido."
          />

          {enrollError && <Alert severity="error" sx={{ mt: 2 }}>{enrollError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTermsOpen(false)} disabled={enrolling}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleConfirmEnroll}
            disabled={!termsAccepted || enrolling}
          >
            {enrolling ? 'Inscribiendo…' : 'Aceptar e inscribirme'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CourseDetailPage;
